import React, { useState } from "react";
import { SIMPLE_PAGES } from "constants/estimatingDatabasePages";
import { useDatabaseWorkspace, useScopedList } from "hooks/useEstimatingDatabase";
import { DatabasePageShell } from "components/EstimatingDatabase/DatabasePageShell";
import { useListPanel } from "components/EstimatingDatabase/useListPanel";
import {
  unitOptions,
  useLookup,
  awpOptions,
  awpDisplay,
  clarificationSubject,
  clarificationSubjectOptions,
} from "components/EstimatingDatabase/lookups";

// General conditions, insurance, requirements, allowances, survey & layout,
// clarifications, AWP, project types, labor types and material classes:
// one list each (two for clarifications and AWP), driven by the field lists
// in constants/estimatingDatabasePages.
export default function SimpleDatabasePage({ pageKey }) {
  const page = SIMPLE_PAGES[pageKey];
  const workspace = useDatabaseWorkspace();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("main");

  const main = useScopedList(workspace, page);
  const secondary = useScopedList(workspace, page.secondary || { fields: [] }, {
    enabled: !!page.secondary,
  });

  // The subject and AWP lists the desktop resolves these pages' references
  // against (ClarificationTypeUtil / AwpUtil): every row of the company,
  // whatever the version, BidLight's records included - not the scoped
  // list on the other tab, which in the company scope is empty until the
  // company copies its own.
  const fields = [...(page.fields || []), ...((page.secondary && page.secondary.fields) || [])];
  const uses = (kind) => fields.some((f) => f.options === kind);
  const clarificationTypes = useLookup(workspace, "clarification_types", {
    enabled: uses("clarificationTypes"),
    refreshKey: pageKey === "clarifications" ? secondary.rawRows : undefined,
  });
  const awps = useLookup(workspace, "awps", {
    enabled: uses("awps"),
    refreshKey: pageKey === "awp" ? main.rawRows : undefined,
  });

  const options = (field) => {
    const extraUnits = main.items.map((it) => it.rec[field.key]);
    switch (field.options) {
      case "time":
      case "units":
      case "unitsAndTime":
        return unitOptions(field.options, workspace.template, extraUnits);
      case "clarificationTypes":
        return clarificationSubjectOptions(clarificationTypes);
      case "awps":
        return awpOptions(awps);
      default:
        return [];
    }
  };

  const optionLabel = (field, v) => {
    if (v === undefined || v === null || v === "") return "--";
    if (field.options === "clarificationTypes") {
      const hit = clarificationSubject(clarificationTypes, v);
      return hit && hit.Description ? hit.Description : String(v);
    }
    if (field.options === "awps") return awpDisplay(awps, v);
    return String(v);
  };

  const firstSubject = () => {
    const opts = clarificationSubjectOptions(clarificationTypes);
    return opts.length ? { Group: opts[0].value } : {};
  };

  const mainPanel = useListPanel({
    list: main,
    config: page,
    workspace,
    search,
    options,
    optionLabel,
    // ClarificationsDatabaseViewModel.CopyToNew: a new clarification starts
    // on the first subject of ClarificationTypeUtil.Items.
    newRecord: pageKey === "clarifications" ? () => firstSubject() : undefined,
  });
  const secondaryPanel = useListPanel({
    list: secondary,
    config: page.secondary || { fields: [], searchFields: [] },
    workspace,
    search,
    options,
    optionLabel,
  });

  const panel = tab === "main" || !page.secondary ? mainPanel : secondaryPanel;
  const lists = page.secondary ? [secondary, main] : [main];

  return (
    <DatabasePageShell
      title={page.title}
      workspace={workspace}
      lists={lists}
      searchValue={search}
      setSearchValue={setSearch}
      onAdd={panel.onAdd}
      onEdit={panel.onEdit}
      onDelete={panel.onDelete}
      onClone={panel.onClone}
      onExport={panel.onExport}
      onImport={panel.onImport}
    >
      {page.secondary && (
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link btn btn-link ${tab === "main" ? "active" : ""}`}
              onClick={() => setTab("main")}
            >
              {page.title}
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link btn btn-link ${tab === "secondary" ? "active" : ""}`}
              onClick={() => setTab("secondary")}
            >
              {page.secondary.tab}
            </button>
          </li>
        </ul>
      )}
      {panel.view}
    </DatabasePageShell>
  );
}
